import React from 'react'
import { Flex, Text } from '@workwhile/ui'
import { useBookingDetailsContextValue } from '../context/BookingDetailsProvider'
import { ButtonWithConfirmation } from '../../ButtonWithConfirmation'
import { useBookingShiftDetailContextValue } from './BookingShiftDetail/context/BookingShiftDetailProvider'
import { isAfter, isPast } from 'date-fns'
import { DAYFORCE_SHIFT_TYPES_ACTIVE } from 'pages/Dayforce/constants'
import { WILL_OR_HAS_WORKED_STATUSES } from 'lib/work'
import { CreateContestedHoursRequestButton } from './CreateContestedHoursRequestButton'
import { CreateWorkerSubmissionRequestButton } from './CreateWorkerSubmissionRequestButton'

export const DayforceWorkerActions = ({ workerId }: { workerId: number }) => {
  const {
    state: {
      dayforceBooking,
      scheduleWorkItemUpdateMutationState,
      cancelContestedHoursRequestMutationState,
    },
    actions: { scheduleWorkItemUpdate, cancelContestedHoursRequest },
  } = useBookingDetailsContextValue()
  const {
    state: { workWhileShift },
  } = useBookingShiftDetailContextValue()

  if (!workWhileShift) {
    return null
  }

  const workWhileAssoc = workWhileShift.work.find(
    (work) => work.worker.id === workerId
  )

  const dayforceWorker = dayforceBooking?.workers.find(
    (worker) => worker.userId === workerId
  )

  const workerWillOrHasWorked = WILL_OR_HAS_WORKED_STATUSES.includes(
    workWhileAssoc?.status ?? 'unknown'
  )

  const shiftHasStarted = isAfter(new Date(), workWhileShift.startsAt)
  const isShiftInThePast = isPast(workWhileShift.endsAt)
  const isWorkerAssignedInDayforce =
    dayforceWorker &&
    DAYFORCE_SHIFT_TYPES_ACTIVE.includes(dayforceWorker.shiftType)

  const showWorkerSubmissionButton =
    workerWillOrHasWorked && !isWorkerAssignedInDayforce && isShiftInThePast

  const initialRequestedBreakDuration =
    workWhileShift.lunchBreak?.type === 'unpaid'
      ? (workWhileShift.lunchBreak?.durationInMinutes ?? 0) / 60
      : null

  const hasPendingContestedHoursRequest =
    dayforceWorker?.pendingContestedHoursRequest

  return (
    <Flex gap={2} flexDirection="row" justifyContent="flex-end">
      {workWhileAssoc ? (
        <>
          {workerWillOrHasWorked &&
          !isWorkerAssignedInDayforce &&
          shiftHasStarted &&
          !isShiftInThePast ? (
            <Text>Wait until shift ends to assign worker</Text>
          ) : null}
          {showWorkerSubmissionButton ? (
            <CreateWorkerSubmissionRequestButton
              associationId={workWhileAssoc.id}
            />
          ) : null}
          {hasPendingContestedHoursRequest ? (
            <ButtonWithConfirmation
              message="Are you sure you want to cancel this contested hours request?"
              variant="secondary"
              kind="small"
              onClick={() =>
                cancelContestedHoursRequest({
                  associationId: workWhileAssoc.id,
                })
              }
              style={{ padding: '0 0.5rem' }}
              disabled={cancelContestedHoursRequestMutationState.isPending}
              loading={cancelContestedHoursRequestMutationState.isPending}
              danger
            >
              <Text fontSize="12px">Cancel contest</Text>
            </ButtonWithConfirmation>
          ) : (
            <CreateContestedHoursRequestButton
              associationId={workWhileAssoc.id}
              workerName={workWhileAssoc.worker.name}
              shiftStartsAt={workWhileShift.startsAt}
              shiftEndsAt={workWhileShift.endsAt}
              startedAt={workWhileAssoc.startedAt}
              finishedAt={workWhileAssoc.completedAt}
              initialRequestedStartedAt={workWhileAssoc.startedAt}
              initialRequestedFinishedAt={workWhileAssoc.completedAt}
              initialRequestedBreakDuration={initialRequestedBreakDuration}
            />
          )}
          <ButtonWithConfirmation
            message="Are you sure you want to refresh this worker's work item in Dayforce? Any changes might take a few minutes to reflect in the system."
            variant="secondary"
            kind="small"
            onClick={() =>
              scheduleWorkItemUpdate({
                associationId: workWhileAssoc.id,
                shiftId: workWhileShift.id,
              })
            }
            style={{ padding: '0 0.5rem' }}
            disabled={scheduleWorkItemUpdateMutationState.isPending}
            loading={scheduleWorkItemUpdateMutationState.isPending}
          >
            <Text fontSize="12px">Sync systems</Text>
          </ButtonWithConfirmation>
        </>
      ) : (
        <Text>Worker not found in WorkWhile</Text>
      )}
    </Flex>
  )
}
